import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFileWord,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb, { BreadcrumbItem } from "../common/breadcrumb";
import {
  ColdStorageFileNode,
  ColdStorageFolderNode,
  ColdStorageTree,
} from "../../types/coldStorage";
import {
  isNasPreviewableFile,
  NAS_VIEW_UNAVAILABLE_MESSAGE,
} from "../../utils/nasPreviewUtils";
import { formatDocumentUploadInfo } from "../../utils/documentUploadInfo";

interface NasFolderTreeProps {
  tree: ColdStorageTree;
  onViewFile?: (file: ColdStorageFileNode) => void;
  onDownloadFile?: (file: ColdStorageFileNode) => void;
}

const HIDDEN_MARKER_NAMES = new Set(["$$folder.placeholder", ".structure-created"]);

const headerCellSx = {
  fontWeight: "bold",
  textAlign: "center",
  color: "#fff",
};

const bodyCellSx = {
  color: "#fff",
  py: 1,
};

const isHiddenMarker = (name: string) =>
  HIDDEN_MARKER_NAMES.has((name ?? "").trim().toLowerCase());

const visibleFiles = (files: ColdStorageFileNode[] | undefined) =>
  (files ?? []).filter((file) => !isHiddenMarker(file.name));

const countDocuments = (folder: ColdStorageFolderNode): number =>
  visibleFiles(folder.files).length
  + (folder.children ?? []).reduce((total, child) => total + countDocuments(child), 0);

const getFileIcon = (fileName: string) => {
  const name = (fileName ?? "").toLowerCase();
  if (name.endsWith(".doc") || name.endsWith(".docx")) {
    return faFileWord;
  }
  if (
    name.endsWith(".jpg")
    || name.endsWith(".jpeg")
    || name.endsWith(".png")
    || name.endsWith(".gif")
    || name.endsWith(".tif")
    || name.endsWith(".tiff")
    || name.endsWith(".heic")
    || name.endsWith(".heif")
  ) {
    return faFileImage;
  }
  if (name.endsWith(".pdf")) {
    return faFilePdf;
  }
  if (name.endsWith(".xls") || name.endsWith(".xlsx")) {
    return faFileExcel;
  }
  return faFile;
};

const getMimeType = (fileName: string) => {
  const extension = (fileName.includes(".") ? fileName.split(".").pop() : "")?.toLowerCase();
  switch (extension) {
    case "pdf":
      return "application/pdf";
    case "doc":
      return "application/msword";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "xls":
      return "application/vnd.ms-excel";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    default:
      return extension ? `application/${extension}` : "application/octet-stream";
  }
};

const formatSizeKb = (sizeBytes: number) => `${((sizeBytes ?? 0) / 1024).toFixed(2)} KB`;

const findFolderByPath = (
  path: string[],
  folders: ColdStorageFolderNode[]
): ColdStorageFolderNode | null => {
  if (path.length === 0) {
    return null;
  }

  let currentFolders = folders;
  let current: ColdStorageFolderNode | null = null;
  for (const name of path) {
    current = currentFolders.find((folder) => folder.name === name) ?? null;
    if (!current) {
      return null;
    }

    currentFolders = current.children ?? [];
  }

  return current;
};

const NasFolderTree: React.FC<NasFolderTreeProps> = ({
  tree,
  onViewFile,
  onDownloadFile,
}) => {
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [hideEmptyFolders, setHideEmptyFolders] = useState(false);

  useEffect(() => {
    setCurrentPath([]);
  }, [tree.root]);

  const currentFolder = useMemo(
    () => findFolderByPath(currentPath, tree.folders ?? []),
    [currentPath, tree.folders]
  );

  const foldersInView = (currentFolder ? currentFolder.children ?? [] : tree.folders ?? [])
    .filter((folder) => !hideEmptyFolders || countDocuments(folder) > 0);
  const filesInView = visibleFiles(currentFolder ? currentFolder.files : tree.files);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "All Documents", path: [], isClickable: currentPath.length > 0 },
    ...currentPath.map((name, index) => ({
      label: name,
      path: currentPath.slice(0, index + 1),
      isClickable: index < currentPath.length - 1,
    })),
  ];

  const openFolder = (folderName: string) => {
    setCurrentPath([...currentPath, folderName]);
  };

  return (
    <Paper
      sx={{
        height: "calc(100%-32px)",
        p: 2,
        border: "1px solid white",
        background: "none",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          minHeight: 44,
          mb: 1,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideEmptyFolders}
              onChange={(e) => setHideEmptyFolders(e.target.checked)}
              sx={{
                width: 52,
                height: 30,
                padding: 0,
                "& .MuiSwitch-switchBase": {
                  padding: 1,
                  color: "rgba(255,255,255,0.9)",
                  "&.Mui-checked": {
                    transform: "translateX(22px)",
                    color: "#fff",
                  },
                },
                "& .MuiSwitch-track": {
                  borderRadius: 20,
                  backgroundColor: "rgba(255,255,255,0.28)",
                  opacity: 1,
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "hsl(19 101% 51% / 1)",
                  opacity: 1,
                },
              }}
            />
          }
          label="Hide empty folders"
          sx={{
            m: 0,
            "& .MuiFormControlLabel-label": {
              color: "#fff",
              fontSize: "0.9rem",
              marginLeft: "0.5rem",
            },
          }}
        />
      </Box>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ mb: 1, color: "hsl(19 101% 51% / 1)" }}
      >
        NAS Docs — {tree.root}
      </Typography>
      <Breadcrumb items={breadcrumbItems} onNavigate={setCurrentPath} />
      {foldersInView.length === 0 && filesInView.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          No folders or files in this NAS location.
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: "calc(100% - 96px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="none" sx={headerCellSx}>
                  Icon
                </TableCell>
                <TableCell padding="none" sx={headerCellSx}>
                  Name
                </TableCell>
                <TableCell padding="none" sx={headerCellSx}>
                  Type
                </TableCell>
                <TableCell padding="none" sx={headerCellSx}>
                  Size/Details
                </TableCell>
                <TableCell padding="none" sx={headerCellSx}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {foldersInView.map((folder) => (
                <TableRow key={folder.path} hover>
                  <TableCell padding="none" sx={{ ...bodyCellSx, textAlign: "center" }}>
                    <FolderIcon sx={{ color: "hsl(19 101% 51% / 1)" }} />
                  </TableCell>
                  <TableCell padding="none" sx={bodyCellSx}>
                    <Box
                      component="button"
                      sx={{
                        fontWeight: 700,
                        color: "#fff",
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        textAlign: "left",
                        fontSize: "inherit",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => openFolder(folder.name)}
                    >
                      {folder.name}
                    </Box>
                  </TableCell>
                  <TableCell padding="none" align="center" sx={bodyCellSx}>
                    Folder
                  </TableCell>
                  <TableCell padding="none" align="center" sx={bodyCellSx}>
                    {countDocuments(folder)} documents
                  </TableCell>
                  <TableCell padding="none" align="center" sx={bodyCellSx}>
                    <IconButton
                      sx={{ color: "#fff" }}
                      onClick={() => openFolder(folder.name)}
                      title="Open folder"
                      aria-label={`Open folder ${folder.name}`}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filesInView.map((file) => (
                <TableRow key={file.path} hover>
                  <TableCell padding="none" sx={{ ...bodyCellSx, textAlign: "center" }}>
                    <FontAwesomeIcon
                      icon={getFileIcon(file.name)}
                      style={{ color: "#fff", fontSize: "1.2rem" }}
                    />
                  </TableCell>
                  <TableCell padding="none" sx={bodyCellSx}>
                    {file.name}
                  </TableCell>
                  <TableCell padding="none" align="center" sx={bodyCellSx}>
                    {getMimeType(file.name)}
                  </TableCell>
                  <TableCell padding="none" align="center" sx={bodyCellSx}>
                    {formatSizeKb(file.sizeBytes)}
                  </TableCell>
                  <TableCell padding="none" align="center" sx={bodyCellSx}>
                    <Tooltip
                      title={
                        isNasPreviewableFile(file.name)
                          ? "View"
                          : NAS_VIEW_UNAVAILABLE_MESSAGE
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          sx={{
                            color: "#fff",
                            "&.Mui-disabled": {
                              color: "rgba(255, 255, 255, 0.4)",
                            },
                          }}
                          onClick={() => onViewFile?.(file)}
                          disabled={!isNasPreviewableFile(file.name)}
                          aria-label={
                            isNasPreviewableFile(file.name)
                              ? `View ${file.name}`
                              : `View unavailable for ${file.name}`
                          }
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      sx={{ color: "#fff" }}
                      onClick={() => onDownloadFile?.(file)}
                      aria-label={`Download ${file.name}`}
                    >
                      <DownloadIcon />
                    </IconButton>
                    <Tooltip
                      title={
                        <span style={{ whiteSpace: "pre-line" }}>
                          {
                            formatDocumentUploadInfo(
                              file.uploadedBy,
                              file.uploadedAtUtc ?? file.lastModified
                            ).tooltip
                          }
                        </span>
                      }
                      arrow
                    >
                      <IconButton
                        sx={{ color: "#fff" }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Upload info for ${file.name}`}
                      >
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default NasFolderTree;
